import { ApolloClient } from '@apollo/client';
import pRetry from 'p-retry';
import { fromPromise } from 'xstate';

import { TsRestClient } from '@eluve/api-client-provider';
import { ScheduleBackgroundTaskAction } from '@eluve/appointment-tasks';
import { isValidTimestamp } from '@eluve/date-utils';
import {
  ApiError,
  HttpError,
  NetworkError,
  ValidationError,
} from '@eluve/eluve-errors';
import { appointmentTotalDurationFragment } from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';
import { CorrelatedLogger } from '@eluve/logger';
import { calculateFileMd5 } from '@eluve/utils';

import { safeGetAudioDurationFromFile } from './getAudioDurationFromFile';
import {
  CheckFileOutput,
  presignedUploadMachine,
} from './presignedUrlUpload.machine';
import { UploadFileArgs } from './uploadFile.machine';

export const getAppointmentTotalDuration = graphql(
  `
    query getAppointmentTotalDuration($appointmentId: uuid!, $tenantId: uuid!) {
      appointmentsByPk(id: $appointmentId, tenantId: $tenantId) {
        __typename
        id
        ...appointmentTotalDuration
      }
    }
  `,
  [appointmentTotalDurationFragment],
);

export const defaultPresignedUploadMachine = ({
  apiClient,
  log,
  apolloClient,
  clientOrigin,
  scheduleBackgroundTask,
}: {
  apiClient: TsRestClient;
  apolloClient: ApolloClient<unknown>;
  log: CorrelatedLogger;
  clientOrigin?: string;
  scheduleBackgroundTask: ScheduleBackgroundTaskAction;
}) => {
  return presignedUploadMachine.provide({
    actors: {
      checkFile: fromPromise<CheckFileOutput, UploadFileArgs>(
        async ({ input }) => {
          const { file, recordingStartedAt } = input;

          if (!isValidTimestamp(recordingStartedAt)) {
            throw new ValidationError('Invalid recording timestamp format');
          }

          const [recordingDuration, fileHash] = await Promise.all([
            safeGetAudioDurationFromFile({ file, log }),
            calculateFileMd5(file),
          ]);

          return {
            recordingDuration,
            fileHash,
          };
        },
      ),
      generatePresignedUrl: fromPromise(async ({ input }) => {
        const {
          tenantId,
          appointmentId,
          file,
          uploadType,
          checkFileOutput: { fileHash },
          format,
          localUploadId,
          networkRetryConfig: { retries, retryDelayMs },
        } = input;

        let fileUploadUrl = '';
        let isUploadRequired = true;
        let bytesAlreadyUploaded: number | undefined;

        let fileUploadId = localUploadId;

        await pRetry(
          async () => {
            const generateSignedUrlResponse =
              await apiClient.appointments.prepareAppointmentFileUpload({
                params: {
                  tenantId,
                  appointmentId,
                },
                body: {
                  fileSize: file.size,
                  fileHash,
                  extension: format.extension,
                  fileType: uploadType,
                  uploadId: localUploadId,
                  fileName: file.name,
                  origin: clientOrigin,
                },
              });

            if (![200, 201].includes(generateSignedUrlResponse.status)) {
              throw new ApiError('Failed to generate signed url');
            }

            if (generateSignedUrlResponse.status === 201) {
              const { uploadURL, uploadId, bytesUploaded } =
                generateSignedUrlResponse.body;
              fileUploadUrl = uploadURL;
              fileUploadId = uploadId;
              bytesAlreadyUploaded = bytesUploaded;
            }
            if (
              generateSignedUrlResponse.status === 200 &&
              generateSignedUrlResponse.body.status === 'FILE_ALREADY_EXISTS'
            ) {
              const { uploadId } = generateSignedUrlResponse.body;
              isUploadRequired = false;
              fileUploadId = uploadId;
            }
          },
          {
            // Allow retry up to 3 times with delay to address intermittent client network issues
            retries,
            shouldRetry: () => navigator.onLine,
            onFailedAttempt: () =>
              new Promise((resolve) => setTimeout(resolve, retryDelayMs)),
          },
        );

        return {
          preSignedUrl: fileUploadUrl,
          isUploadRequired,
          fileUploadId,
          bytesUploaded: bytesAlreadyUploaded,
        };
      }),

      uploadFile: fromPromise(async ({ input }) => {
        const {
          actorRef,
          file,
          fileUploadUrl,
          mimeType,
          bytesUploaded = 0,
          networkRetryConfig: { retries, retryDelayMs },
        } = input;

        await pRetry(
          async () => {
            await new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();

              // Slice file from resume point (upload entire remaining portion)
              const fileToUpload =
                bytesUploaded > 0 ? file.slice(bytesUploaded) : file;
              const startByte = bytesUploaded;
              const endByte = file.size - 1;
              const totalSize = file.size;

              actorRef?.send({
                type: 'UPLOAD_FILE.UPLOADING',
                progress: Math.round((startByte / totalSize) * 100),
              });

              xhr.open('PUT', fileUploadUrl, true);
              xhr.setRequestHeader('Content-Type', mimeType);

              // Add Content-Range header if resuming
              if (bytesUploaded > 0 && bytesUploaded < file.size) {
                xhr.setRequestHeader(
                  'Content-Range',
                  `bytes ${startByte}-${endByte}/${totalSize}`,
                );
              }

              xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                  // Add already-uploaded bytes to progress
                  const totalProgress = startByte + event.loaded;
                  const progress = Math.round(
                    (totalProgress / totalSize) * 100,
                  );
                  actorRef?.send({
                    type: 'UPLOAD_FILE.UPLOADING',
                    progress,
                  });
                }
              });

              xhr.onload = () => {
                // Accept 200/201 for resumable uploads
                if (xhr.status === 200 || xhr.status === 201) {
                  resolve(xhr.response);
                } else {
                  reject(
                    new HttpError(
                      `File upload failed with status ${xhr.status}. Response: ${xhr.responseText}`,
                    ),
                  );
                }
              };

              xhr.onerror = () => {
                reject(
                  new NetworkError(
                    'Failed to upload file due to network error',
                  ),
                );
              };

              xhr.send(fileToUpload);
            });
          },
          {
            retries,
            onFailedAttempt: () =>
              new Promise((resolve) => setTimeout(resolve, retryDelayMs)),
          },
        );
      }),
      markFileComplete: fromPromise(async ({ input }) => {
        const {
          appointmentId,
          tenantId,
          fileUploadId,
          shouldBeTranscribed,
          wasDegraded,
          chunkNum,
          recordingDuration,
          recordingStartedAt,
        } = input;
        const result = await apiClient.appointments.markFileUploadComplete({
          params: {
            tenantId,
            appointmentId,
          },
          body: {
            uploadId: fileUploadId,
            shouldBeTranscribed,
            wasSegmentDegraded: wasDegraded,
            chunkNum,
            recordingDuration,
            recordingStartedAt,
          },
        });

        if (result.status !== 200) {
          throw new ApiError('Failed to mark file upload as complete');
        }

        void apolloClient.query({
          query: getAppointmentTotalDuration,
          variables: {
            appointmentId,
            tenantId,
          },
        });
      }),
    },
    actions: {
      scheduleBackgroundTask,
    },
  });
};
